import { BannerEntity, BannersListEntity } from '@core/entities/banners.entity';
import { PaginatedEntity, Pagination } from '@core/entities';
import { ElementRef, inject } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@ngneat/dialog';
import { UseBanner } from '@core/usecases/useBanner';
import { untilDestroyed } from '@ngneat/until-destroy';
import { dateRangeValidator, EnumToKeyValueArray, GetTeamId, GetUserId, isMobileView, scrollToTop, sortData } from '@core/utils';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { BannerCategory, BannerDisplayOptions, BannerInvoiceStatus, BannerPlacement, BannerStatus, ConfirmationDialogType, DeleteConfirmationOptions } from '@core/enums';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ButtonComponent } from '@shared/components/ui';
import { Logger } from '@core/services';
import { FormService } from '@core/services/misc/form.service';
import { CurrentSort } from '@core/types';
import { DropDownItem } from '@core/types/Dropdown.type';

const log = new Logger('Banner Composition');

export class BannersComposition extends FormService {
  banners: BannerEntity[] = [];
  banner: BannerEntity;
  pagination: Pagination = new PaginatedEntity().pagination;
  invoiceStatuses: DropDownItem[];
  placements: DropDownItem[];
  displayOptions: DropDownItem[];
  categories: DropDownItem[];
  selectedImage: File;
  sortedBanners: BannerEntity[];
  currentSort: CurrentSort = { column: '', direction: '' };
  private readonly _toast = inject(HotToastService);
  private readonly _translate = inject(TranslateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _bottomSheet = inject(NgxBottomSheetService);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastService = inject(HotToastService);
  private readonly _router = inject(Router);
  private readonly _fb: FormBuilder = inject(FormBuilder);

  private readonly _useBanners = new UseBanner();

  constructor() {
    super();

    this.categories = EnumToKeyValueArray(BannerCategory, this._translate);
    this.displayOptions = EnumToKeyValueArray(BannerDisplayOptions, this._translate);
    this.invoiceStatuses = EnumToKeyValueArray(BannerInvoiceStatus);
    this.placements = EnumToKeyValueArray(BannerPlacement);
  }

  get isUpdateMode(): boolean {
    return !!(this.banner && this.banner.id && this.banner?.imageUrl);
  }

  onSortChange({ column, direction }: CurrentSort) {
    this.currentSort = { column, direction };
    this.sortedBanners = sortData(this.banners, column as keyof (typeof this.banners)[0], direction);
  }

  deleteBanner(banner: BannerEntity) {
    if (!banner.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheet.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('Banner'),
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useBanners.deleteBanner(banner.id)).then(() => {
                  this._toast.success(this._translate.instant('Delete Successful'));
                  this.fetchAllBanners();
                });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: banner.bannerName,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useBanners.deleteBanner(banner.id)).then(() => {
              this._toast.success(this._translate.instant('Delete Successful'));
              this.fetchAllBanners();
            });
          }
        },
      });
    }
  }

  isChecked(value: number, controlName: 'placement' | 'display' | 'category'): boolean {
    return this.form.get(controlName).value?.includes(value);
  }

  onCheckboxChange(event: any, controlName: 'placement' | 'display' | 'category') {
    const value = +event.target.value;
    const control = this.form.get(controlName);
    const currentValues: number[] = control.value || [];
    if (event.target.checked) {
      if (!currentValues.includes(value)) {
        control.setValue([...currentValues, value]);
      }
    } else {
      control.setValue(currentValues.filter((val) => val !== value));
    }
    this.markControlDirty(control as FormControl);
  }

  // onStatusChange(event: any): void {
  //   const value = event.target.checked ? BannerStatus.Active : BannerStatus.Inactive;
  //   this.form.get('status').setValue(value, { emitEvent: false });
  // }

  createUpdateBanner(elRef: ElementRef, btnRef?: ButtonComponent, id?: number) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form, elRef);
      return;
    }

    // Loading data from form to mutate fields where required
    const data = this.removeNullObjects(this.form.value) as BannerEntity;
    data.status = this.form.value.status === true ? BannerStatus.Active : BannerStatus.Inactive;
    data.placement = data.placement.sort();

    // Converting data to FormData
    const body = this.jsonToFromData(this.removeEventFields(data), ['placement', 'category']);

    // Appending extra data to the FormData
    body.append('author', GetUserId());
    body.append('team_id', GetTeamId());

    if (this.selectedImage) {
      body.append('file', this.selectedImage);
    }

    const service$ = id ? this._useBanners.updateBanner(id, body) : this._useBanners.addBanner(body);
    const message = id ? this._translate.instant('Update Successful') : this._translate.instant('Created Successfully');

    this.call(service$, btnRef)
      .then((resp) => {
        if (resp) {
          this._toast.success(message);
          this._router.navigate(['/more/banners']);
        } else {
          this._toast.error(this._translate.instant('Something went wrong'));
        }
      })
      .catch(() => {
        this._toast.error(this._translate.instant('Something went wrong'));
        log.error('Error while creating or updating banner');
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected paginationChange(pagination: Pagination) {
    this.pagination = pagination;
    this.isLoading = true;

    this.fetchAllBanners();
  }

  protected fetchAllBanners() {
    this.call(this._useBanners.getAllBanners(this.pagination))
      .then((res: BannersListEntity) => {
        if (res) {
          this.banners = res.banners;
          this.sortedBanners = this.banners;
          this.pagination = res.pagination;
          if (isMobileView()) {
            scrollToTop();
          }
        } else {
          log.info('No banners found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching banners', err);
      });
  }

  protected getBannerById(id: number) {
    this.call(this._useBanners.getBannerById(id)).then((res: BannerEntity) => {
      if (res) {
        this.banner = res;
        this.updateAndValidate(this.form, res).then(() => {
          this.updateRawValuesInForm(this.form, res, ['placement', 'category', 'display']);
          this.updateEnumValueToBooleanInForm(this.form, 'status', BannerStatus.Active, res.status);
          this.form.get('image').setValue(res.imageUrl);
        });
      }
    });
  }

  protected initForm() {
    this.form = this._fb.group(
      {
        bannerName: [null, [Validators.required]],
        description: [null],
        bannerStartDate: [null, [Validators.required]],
        bannerEndDate: [null, [Validators.required]],
        redirectLink: [null, [Validators.required, Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')]],
        status: [true, [Validators.required]],
        category: [null, [Validators.required]],
        invoice: [null, [Validators.required]],
        placement: [null, [Validators.required]],
        display: [[], [Validators.required]],
        image: [null, [Validators.required]],
      },
      { validators: dateRangeValidator('bannerStartDate', 'bannerEndDate') },
    );
  }
}
